/**
 * 되돌리기(undo) 그룹에 기록된 실제 편집 위치를 찾는다.
 * 위치 기록이 없는 예전 undo entry만 화면상 가장 위의 연동 셀로 fallback한다.
 */

import type { IxdChunk } from "@/apis/ixd-editor/types";
import {
  parseCellIdChangeKey,
  parseIdentityChangeKey,
  type CellIdentityLocation,
} from "@/utils/ixd-cell-model";
import type { CellChangeReference } from "@/hooks/ixd-edit-session/edit-session-store";

interface FindTopmostChangeLocationInput {
  /** 화면 표시 순서의 chunk 목록 — 이 순서가 곧 세로 배치 순서다. */
  chunks: IxdChunk[];
  identityLocationsByKey: ReadonlyMap<string, readonly CellIdentityLocation[]>;
  identityKeyByNormalizedCellId: ReadonlyMap<string, string>;
}

type LocationRank = [chunkIndex: number, dlViewIndex: number, rowIndex: number];

const isHigher = (a: LocationRank, b: LocationRank): boolean => {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return a[i] < b[i];
  }
  return false;
};

export const findTopmostChangeLocation = (
  changes: readonly CellChangeReference[],
  {
    chunks,
    identityLocationsByKey,
    identityKeyByNormalizedCellId,
  }: FindTopmostChangeLocationInput,
): CellIdentityLocation | null => {
  const chunkOrder = new Map(chunks.map((chunk, index) => [chunk.id, index]));
  let best: CellIdentityLocation | null = null;
  let bestRank: LocationRank | null = null;
  let preferredBest: CellIdentityLocation | null = null;
  let preferredBestRank: LocationRank | null = null;

  for (const { changeKey, locationUids = [] } of changes) {
    const locationUidSet = new Set(locationUids);
    const cellId = parseCellIdChangeKey(changeKey);
    const identityKey =
      parseIdentityChangeKey(changeKey) ??
      (cellId ? identityKeyByNormalizedCellId.get(cellId) : undefined);
    if (!identityKey) continue;

    for (const location of identityLocationsByKey.get(identityKey) ?? []) {
      const chunkIndex = chunkOrder.get(location.chunkId);
      if (chunkIndex === undefined) continue;
      const rank: LocationRank = [
        chunkIndex,
        location.dlViewIndex,
        location.rowIndex ?? 0,
      ];
      if (bestRank === null || isHigher(rank, bestRank)) {
        bestRank = rank;
        best = location;
      }
      if (
        location.uid &&
        locationUidSet.has(location.uid) &&
        (preferredBestRank === null || isHigher(rank, preferredBestRank))
      ) {
        preferredBestRank = rank;
        preferredBest = location;
      }
    }
  }

  return preferredBest ?? best;
};
